var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Banner, Box, FlexBox, Modal, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useState, } from 'react';
import { useLocation } from 'react-router-dom';
import { companiesApi } from 'apis/companies';
import { contactsApi } from 'apis/contacts';
import { usersApi } from 'apis/users';
import { analytics } from 'app/utils/segment';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { actions as conversationsActions } from 'components/conversation/actions/conversations';
import { notificationActions } from 'components/notifications/actions/notification';
import { getRequestID } from 'components/shared/util/request';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { CompareContacts } from './components/compareContacts/compareContacts';
import { PreviewContact } from './components/previewContact/previewContact';
import { SelectContact } from './components/selectContact/selectContact';
import styles from './mergeContactsModal.module.scss';
import { getMappedMergeDryRunErrors } from './utils/mapMergeDryRunErrors';
/** A component that displays a modal for merging contacts (end users) */
export const MergeContactsModal = createAppModal({ type: 'app:mergeContacts', featureFlag: 'merge-contacts' }, ({ isOpen, modal, hide }) => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const [mergeContacts, { isLoading: isMergingContacts }] = contactsApi.useMergeContactsMutation();
    const [getUser, { isLoading: isLoadingUser }] = usersApi.useLazyReadUserQuery();
    const [getCompany] = companiesApi.useLazyReadCompanyQuery();
    const notify = useNotifications();
    const [targetContact, setTargetContact] = useState();
    const [sourceContact, setSourceContact] = useState();
    const [previewContact, setPreviewContact] = useState();
    const [errorMessages, setErrorMessages] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const { user: modalArgUser } = modal.arg;
    useEffect(() => {
        if (!modalArgUser.company_id) {
            setTargetContact(modalArgUser);
            return;
        }
        (() => __awaiter(void 0, void 0, void 0, function* () {
            if (!modalArgUser.company_id)
                return;
            try {
                const { data } = yield getCompany({
                    id: modalArgUser.company_id,
                }, true).unwrap();
                setTargetContact(Object.assign(Object.assign({}, modalArgUser), { company: data }));
            }
            catch (error) {
                setErrorMessages(error.data.errors.map((unknownError) => ({
                    description: unknownError,
                })));
            }
        }))();
    }, [getCompany, modalArgUser]);
    useEffect(() => {
        if (isOpen) {
            analytics.mergeContactsModalOpened();
        }
        return () => {
            analytics.mergeContactsModalClosed();
        };
    }, [isOpen]);
    const handleCancel = useCallback(() => {
        hide === null || hide === void 0 ? void 0 : hide();
        if (sourceContact) {
            analytics.mergeContactsMergeCancelled();
        }
    }, [hide, sourceContact]);
    const getAndSetSourceContact = useCallback((contact) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const results = yield Promise.allSettled([
            getUser({ id: contact.id }, true).unwrap(),
            ...[
                contact.company_id
                    ? getCompany({
                        id: contact.company_id,
                    }, true).unwrap()
                    : null,
            ],
        ]);
        /** If the getUser fails */
        if (results[0].status === 'rejected') {
            throw results[0].reason;
        }
        /** If the getCompany fails */
        if (results[1].status === 'rejected') {
            throw results[1].reason;
        }
        setSourceContact(Object.assign(Object.assign({}, results[0].value.data), { company: (_a = results[1].value) === null || _a === void 0 ? void 0 : _a.data }));
    }), [getCompany, getUser]);
    const dryMergeAndSetPreviewContact = useCallback((contact) => __awaiter(void 0, void 0, void 0, function* () {
        if (!targetContact)
            return;
        const { data: mergedContact } = yield mergeContacts({
            target: targetContact.id,
            dryRun: true,
            sources: [contact.id],
        }).unwrap();
        if (mergedContact.company_id) {
            const { data: companyData } = yield getCompany({
                id: mergedContact.company_id,
            }, true).unwrap();
            setPreviewContact(Object.assign(Object.assign({}, mergedContact), { company: companyData }));
        }
        else {
            setPreviewContact(mergedContact);
        }
    }), [getCompany, mergeContacts, targetContact]);
    const handleOnContactChange = useCallback((contact) => __awaiter(void 0, void 0, void 0, function* () {
        var _b, _c, _d;
        if (!targetContact)
            return;
        setErrorMessages([]);
        setSourceContact(undefined);
        setPreviewContact(undefined);
        analytics.mergeContactsSourceContactSelected();
        try {
            const results = yield Promise.allSettled([
                getAndSetSourceContact(contact),
                dryMergeAndSetPreviewContact(contact),
            ]);
            results.forEach((result) => {
                if (result.status === 'rejected') {
                    throw result.reason;
                }
            });
        }
        catch (error) {
            const { data } = error;
            const errorMessages = [];
            if (!data) {
                errorMessages.push({ description: JSON.stringify(error) });
            }
            if (data && !((_b = data.causes) === null || _b === void 0 ? void 0 : _b.length)) {
                errorMessages.push({ description: data.title });
            }
            if ((_c = data.causes) === null || _c === void 0 ? void 0 : _c.length) {
                const errors = getMappedMergeDryRunErrors(data.causes, [
                    targetContact,
                    contact,
                ]).map((dryRunMergeError) => ({
                    title: dryRunMergeError.title,
                    description: dryRunMergeError.detail,
                }));
                errorMessages.push(...errors);
            }
            if ((_d = data === null || data === void 0 ? void 0 : data.errors) === null || _d === void 0 ? void 0 : _d.length) {
                const errors = data.errors.map((errorMessage) => ({
                    description: errorMessage,
                }));
                errorMessages.push(...errors);
            }
            setErrorMessages(errorMessages);
        }
    }), [dryMergeAndSetPreviewContact, getAndSetSourceContact, targetContact]);
    const handleMerge = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!sourceContact || !targetContact)
            return;
        try {
            const { data: mergedContact } = yield mergeContacts({
                target: targetContact.id,
                sources: [sourceContact.id],
                dryRun: false,
            }).unwrap();
            const notificationID = getRequestID();
            const targetContactRoute = `/contacts/user/${targetContact === null || targetContact === void 0 ? void 0 : targetContact.id}`;
            const isTargetContactRoute = location.pathname === targetContactRoute;
            notify(Object.assign(Object.assign({ id: notificationID, title: 'Contact merged!', text: `Your contacts are now merged. ${!isTargetContactRoute
                    ? 'If you want to further edit the new contact, click "go to contact".'
                    : ''}`, className: 'success', icon: 'i-done', time: 5000 }, (!isTargetContactRoute && {
                leftAction: {
                    action: {
                        navigate: `/contacts/user/${targetContact === null || targetContact === void 0 ? void 0 : targetContact.id}`,
                    },
                    text: 'Go to contact',
                },
            })), { rightAction: {
                    text: 'Dismiss',
                    action: () => {
                        dispatch(notificationActions.notificationRemoveById({
                            id: notificationID,
                        }));
                    },
                } }), NotificationType.Success);
            handleCancel();
            analytics.mergeContactsMergeSucceeded();
            pubSub.publish('mergeContactsModalUserMerged', mergedContact);
            dispatch(conversationsActions.conversationsUpdateUser({
                data: mergedContact,
            }));
            dispatch(conversationActions.conversationUpdateUser({
                data: mergedContact,
            }));
        }
        catch (error) {
            const { data } = error;
            notify({
                title: 'Merging failed!',
                text: `${data.title} If the problem persists, reach out to support.`,
                className: 'danger',
                icon: 'i-exclamation',
                time: 20000,
            }, NotificationType.Error);
            handleCancel();
            analytics.mergeContactsMergeFailed();
        }
    }), [
        dispatch,
        handleCancel,
        location.pathname,
        mergeContacts,
        notify,
        sourceContact,
        targetContact,
    ]);
    const mergeContactsSteps = useMemo(() => {
        return [
            {
                title: 'Merge Contacts',
                children: targetContact && (_jsx(SelectContact, { targetContact: targetContact, onContactChange: handleOnContactChange })),
                modalFootProps: {
                    className: 'test-merge-contact-footer',
                    confirmName: 'Next',
                    confirmOnClick: () => setCurrentStep((value) => value + 1),
                    confirmProps: {
                        disabled: !!errorMessages.length || isLoadingUser || !sourceContact,
                        className: 'test-next-button',
                    },
                    cancelName: 'Cancel',
                    cancelOnClick: handleCancel,
                },
            },
            {
                title: 'Merge Contacts',
                children: targetContact && sourceContact && previewContact && (_jsx(CompareContacts, { targetContact: targetContact, sourceContact: sourceContact, previewContact: previewContact })),
                modalFootProps: {
                    className: 'test-merge-contact-footer',
                    confirmName: 'Next',
                    confirmOnClick: () => setCurrentStep((value) => value + 1),
                    cancelName: 'Back',
                    cancelOnClick: () => setCurrentStep((value) => value - 1),
                },
            },
            {
                title: 'Merge Contacts',
                children: targetContact && sourceContact && previewContact && (_jsx(PreviewContact, { targetContact: targetContact, sourceContact: sourceContact, previewContact: previewContact })),
                modalFootProps: {
                    className: 'test-merge-contact-footer',
                    confirmName: 'Merge',
                    confirmOnClick: handleMerge,
                    cancelName: 'Back',
                    cancelOnClick: () => setCurrentStep((value) => value - 1),
                    confirmProps: {
                        isLoading: isMergingContacts,
                    },
                },
            },
        ];
    }, [
        targetContact,
        handleOnContactChange,
        errorMessages.length,
        isLoadingUser,
        sourceContact,
        handleCancel,
        previewContact,
        handleMerge,
        isMergingContacts,
    ]);
    return (_jsxs(Modal, Object.assign({ isOpen: isOpen, onClose: handleCancel, className: classnames(styles.modal, {
            [styles.modalEnlarged]: currentStep !== 0,
        }) }, { children: [_jsx(ModalHead, { title: mergeContactsSteps[currentStep].title, onClose: handleCancel }), _jsxs(ModalBody, { children: [targetContact &&
                        mergeContactsSteps.map((step, index) => (_jsx(Box, Object.assign({ style: { display: index === currentStep ? 'block' : 'none' } }, { children: step.children }), `step-${index}`))), !!errorMessages.length && (_jsx(FlexBox, Object.assign({ gap: "200", flexDirection: "column" }, { children: errorMessages.map((error, index) => (_jsx(Banner, { variant: "fail", headline: error.title, description: _jsx(Text.Paragraph, { children: error.description }) }, `error-${index}`))) })))] }), _jsx(ModalFoot, Object.assign({}, mergeContactsSteps[currentStep].modalFootProps))] })));
});
